import { ColumnInfo, ColumnInfoList, DisplaySetting } from 'components/shared/display_setting'
import { TableCell } from 'components/shared/widget'
import { Institution } from 'entities/institution'
import { ReactNode, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Form } from 'utils/hooks'
import { InstitutionViewModel } from 'view_models/institution_view_model'

export const institutionColumnInfoList = (form?: Form<Record<string, unknown>>): ColumnInfoList => {
    return {
        tableName: '',
        values: [
            { name: 'ID', th: true, width: 50, form: form, orderAttr: 'institution_id', searchType: { name: 'id', type: 'multiple_text' } },
            { name: '施設コード', th: true, width: 90, align: 'center', form: form, orderAttr: 'institution_code', searchType: { name: 'code', type: 'text' } },
            { name: '施設名', th: true, width: 250, form: form, orderAttr: 'institution_name', searchType: { name: 'nameIn', type: 'multiple_text' } },
            {
                name: '種類',
                th: true,
                width: 120,
                align: 'center',
                form: form,
                orderAttr: 'institution_institution_type',
                searchType: { name: 'institutionType', type: 'text' },
            },
            { name: '担当者', th: true, width: 120, form: form, searchType: { name: 'personInCharge', type: 'text' }, align: 'center' },
            {
                name: '発送パターン',
                th: true,
                width: 120,
                align: 'center',
                form: form,
                orderAttr: 'institution_shipping_type',
                searchType: { name: 'shippingType', type: 'text' },
            },

            {
                name: '海外用キット',
                th: true,
                width: 100,
                align: 'center',
                form: form,
                orderAttr: 'institution_is_foreign_country',
                searchType: { name: 'isForeignCountry', type: 'boolean' },
            },
            {
                name: 'キット自動発送',
                th: true,
                width: 120,
                align: 'center',
                form: form,
                orderAttr: 'institution_is_auto_shipping',
                searchType: { name: 'isAutoShipping', type: 'boolean' },
            },
            { name: '依頼書残数（SCAN）', th: true, width: 120, align: 'center', form: form, orderAttr: 'remaining_percent' },
            { name: '依頼書残数（LITE）', th: true, width: 120, align: 'center', form: form, orderAttr: 'remaining_percent' },
            { name: '依頼書残数（CHECK）', th: true, width: 120, align: 'center', form: form, orderAttr: 'remaining_percent' },
            { name: '猶予日数', th: true, width: 80, align: 'center', form: form, orderAttr: 'institution_grace_days' },
            {
                name: '結果返送パターン',
                th: true,
                width: 120,
                align: 'center',
                form: form,
                orderAttr: 'institution_result_packing_type',
                searchType: { name: 'resultPackingType', type: 'text' },
            },
            { name: 'ユーザー数', th: true, width: 120, align: 'center' },
            { name: '医者数', th: true, width: 60, align: 'center' },
            {
                name: '契約日',
                th: true,
                width: 90,
                form: form,
                orderAttr: 'institution_contract_date',
                searchType: { name: 'contract_date', type: 'date' },
            },
            {
                name: '電話番号',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'institution_tel',
                searchType: { name: 'tel', type: 'text' },
            },
            {
                name: 'メールアドレス',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'institution_email',
                searchType: { name: 'email', type: 'text' },
            },
            {
                name: '住所',
                th: true,
                minWidth: 300,
                form: form,
                orderAttr: 'institution_address_id',
                searchType: { name: 'address', type: 'text' },
            },
            { name: '製品', th: true, width: 220 },
        ],
    }
}

export type InstitutionTableDataProps = {
    institution?: Institution
    displaySetting: DisplaySetting
    onClickRequestForm: () => void
    hovered: boolean
}

export const InstitutionTableData = ({ institution, displaySetting, onClickRequestForm, hovered }: InstitutionTableDataProps): JSX.Element => {
    if (!institution) {
        return (
            <>
                {displaySetting.columnInfoList.values.map((_: ColumnInfo, index: number) => {
                    if (displaySetting.hiddenList.includes(index)) {
                        return <></>
                    } else {
                        return <TableCell key={index}></TableCell>
                    }
                })}
            </>
        )
    }
    const vm = new InstitutionViewModel(institution)

    const createCells = useMemo(() => {
        return (
            <>
                <TableCell>{institution.id}</TableCell>
                <TableCell align="center">{institution.code}</TableCell>
                <TableCell>
                    {institution.url ? (
                        <a target="_blank" rel="noreferrer" href={institution.url}>
                            {institution.name}
                        </a>
                    ) : (
                        institution.name
                    )}
                </TableCell>
                <TableCell align="center">
                    {institution.institutionType === 'CLINIC' ? 'クリニック' : institution.institutionType === 'HEALTH_CHECKUP_CENTER' ? '健診センター' : ''}
                </TableCell>
                <TableCell align="center">{institution.personInCharge}</TableCell>
                <TableCell align="center">{institution.shippingType}</TableCell>
                <TableCell align="center">
                    <input type="checkbox" checked={institution.isForeignCountry} />
                </TableCell>
                <TableCell align="center">
                    <input type="checkbox" checked={institution.isAutoShipping} />
                </TableCell>
                <TableCell align="center">{vm.remainingRequestFormProgressView('scan', hovered, onClickRequestForm)}</TableCell>
                <TableCell align="center">{vm.remainingRequestFormProgressView('lite', hovered, onClickRequestForm)}</TableCell>
                <TableCell align="center">{vm.remainingRequestFormProgressView('check', hovered, onClickRequestForm)}</TableCell>
                <TableCell align="center">{institution.graceDays}日</TableCell>
                <TableCell align="center">{institution.resultPackingType}</TableCell>
                <TableCell align="center">
                    <Link to={`patients?institutionId=${institution.id}`}>{institution.patientCount} 人</Link>
                </TableCell>
                <TableCell align="center">
                    <Link to={`doctors?institutionId=${institution.id}`} target="_blank">
                        {institution.doctorCount} 人
                    </Link>
                </TableCell>
                <TableCell>{vm.contractDateText()}</TableCell>
                <TableCell>{institution.tel}</TableCell>
                <TableCell>{institution.email}</TableCell>
                <TableCell>{vm.addressVm().wholeView()}</TableCell>
                <TableCell>{vm.productItemsView()}</TableCell>
            </>
        )
    }, [displaySetting.hiddenList, hovered])

    return createCells.props.children.filter((_: ReactNode, index: number) => !displaySetting.hiddenList?.includes(index)).map((cell: ReactNode) => cell)
}
